import { Anchor } from '@studio/design-system/button';
import { Callout } from '@studio/design-system/callout';
import type { HtmlAttributes } from '@studio/design-system/props';
import { Protocol } from '@studio/models/video';
import { isRtsp } from '@studio/models/video/rtsp';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { seedRtspHelperFromDiscovery } from '../source/utils.js';
import { useProducer, useStore } from '../store/hooks.js';
import { Local } from './local/index.js';
import { Ndi } from './ndi/index.js';
import { Rtsp } from './rtsp/index.js';
import { Content as _Content } from '#~/common/editor/index.js';
import { DataSTag, Sel } from '#~/stag/tag.js';

/**
 * Shown instead of an empty Configure video body when the candidate has no video
 * configuration this step can edit: no video at all, or a protocol with no editor
 * here. The copy makes no claim about why. The action opens the RTSP helper with the
 * discovered camera's host filled in (`seedRtspHelperFromDiscovery`).
 * Per rules: code-comments-required, reference-rules-in-comments (ai.knowledge/global).
 */
export const NoVideoFallback = (): ReactElement => {
  const producer = useProducer();

  const handleEnterRtsp = useLatestFn(() => {
    producer((draft) => {
      seedRtspHelperFromDiscovery(draft, draft.source.selection);
    });
  });

  return (
    <Callout severity='warning' title='No video stream yet'>
      <Callout.P {...DataSTag(Sel.ConfigVideoDeviceVideo, 'no-video-fallback')}>
        <Anchor onClick={handleEnterRtsp}>Enter this camera&apos;s RTSP stream</Anchor> to continue.
      </Callout.P>
    </Callout>
  );
};

/** Video `<Content>` component. */
export const Content = (props: HtmlAttributes): ReactElement => {
  const protocol = useStore((µ) => µ.candidate.video?.protocol);

  // True when one of the editors below renders; otherwise show the fallback so
  // this step never renders an empty body.
  const hasEditor = isRtsp(protocol) || protocol === Protocol.NDI || protocol === Protocol.Local;

  return (
    <_Content {...props}>
      {isRtsp(protocol) && <Rtsp />}
      {protocol === Protocol.NDI && <Ndi />}
      {protocol === Protocol.Local && <Local />}
      {!hasEditor && <NoVideoFallback />}
    </_Content>
  );
};
